// 超级管理员密码由服务端校验；子账号由 tenants sub-admins login API 校验。

import { loginTenantSuperAdminByAccount } from "../../../api/adminConsoleAuth";
import { getTenantLegacyId, setTenantLegacyId } from "../../../api/tenantContext";
import { ADMIN_BEARER_KEY, PLATFORM_BEARER_KEY, isPlatformTenantImpersonating } from "../../../api/http";
import { loginSubAdmin } from "../../../api/subAdmins";

const AUTH_KEY = "jian_admin_auth";

export type AdminConsoleLoginResult = { ok: true } | { ok: false; error: unknown };

export interface SubAdminUser {
  id: string;
  name: string;
  account: string;
  password: string;
  role: "admin" | "editor" | "viewer";
}

export interface LoggedInUser {
  role: "super" | "admin" | "editor" | "viewer";
  name: string;
  id?: string;
  /** 子账号登录名，用于「密码安全」中校验当前密码 */
  account?: string;
  permissions?: string[];
  accessToken?: string;
  tenantLegacyId?: string;
}

export const adminAuth = {
  isLoggedIn(): boolean {
    return localStorage.getItem(AUTH_KEY) !== null;
  },

  /**
   * 手机端等活动页：仅当具备可调租户后台接口的身份时视为 true。
   * - 超级管理员：须同时存在会话与 fanhub_admin_bearer（避免仅残留 jian_admin_auth）
   * - 子账号：jian_admin_auth 且角色为 admin/editor/viewer
   * - 平台代管租户且持有平台 Token
   */
  isTenantBackofficeLoggedIn(): boolean {
    if (this.isLoggedIn()) {
      const u = this.getLoggedInUser();
      if (u?.role === "super") {
        try {
          return !!localStorage.getItem(ADMIN_BEARER_KEY);
        } catch {
          return false;
        }
      }
      if (u && (u.role === "admin" || u.role === "editor" || u.role === "viewer")) {
        try {
          return !!localStorage.getItem(ADMIN_BEARER_KEY);
        } catch {
          return false;
        }
      }
    }
    try {
      if (isPlatformTenantImpersonating() && sessionStorage.getItem(PLATFORM_BEARER_KEY)) return true;
    } catch {
      /* ignore */
    }
    return false;
  },
  getLoggedInUser(): LoggedInUser | null {
    const data = localStorage.getItem(AUTH_KEY);
    if (!data) return null;
    try {
      return JSON.parse(data) as LoggedInUser;
    } catch {
      return null;
    }
  },

  async login(superAdminAccount: string, password: string): Promise<AdminConsoleLoginResult> {
    try {
      const res = await loginTenantSuperAdminByAccount(superAdminAccount.trim(), password.trim());
      const token = res.accessToken;
      const tid = (res.tenantLegacyId ?? superAdminAccount).trim();
      if (tid) setTenantLegacyId(tid);
      localStorage.setItem(ADMIN_BEARER_KEY, token);
      localStorage.setItem(
        AUTH_KEY,
        JSON.stringify({
          role: "super",
          name: "超级管理员",
          accessToken: token,
          tenantLegacyId: tid,
        }),
      );
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e };
    }
  },

  async loginWithAccount(
    account: string,
    password: string,
    tenantLegacyId?: string,
  ): Promise<AdminConsoleLoginResult> {
    try {
      const tidInput = (tenantLegacyId ?? getTenantLegacyId()).trim();
      if (!tidInput) {
        return { ok: false, error: new Error("请先填写租户编号") };
      }
      const res = await loginSubAdmin(tidInput, account.trim(), password);
      const role =
        res.role === "admin" || res.role === "editor" || res.role === "viewer" ? res.role : "viewer";
      const tid = (res.tenantLegacyId ?? tidInput).trim();
      if (tid) setTenantLegacyId(tid);
      if (res.accessToken) {
        localStorage.setItem(ADMIN_BEARER_KEY, res.accessToken);
      } else {
        localStorage.removeItem(ADMIN_BEARER_KEY);
      }
      localStorage.setItem(
        AUTH_KEY,
        JSON.stringify({
          role,
          name: res.name,
          id: res.legacyId,
          account: res.account,
          permissions: res.permissions ?? [],
          accessToken: res.accessToken,
          tenantLegacyId: tid,
        }),
      );
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e };
    }
  },

  logout() {
    localStorage.removeItem(AUTH_KEY);
    localStorage.removeItem(ADMIN_BEARER_KEY);
  },
};
